// RowActionsMenu.jsx — ticket 01 (layer-panel-rework): reusable 3-dot contextual menu for
// list/table row actions. Not LayerPanel-specific: callers pass an `actions` array and this
// component owns only the trigger IconButton + MUI Menu wiring, so any other row type (views,
// markings, etc.) can reuse it later without duplicating the open/close/anchor plumbing.

import React, {useState} from 'react';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Tooltip from '@mui/material/Tooltip';
import MoreVertRounded from '@mui/icons-material/MoreVertRounded';

/**
 * @param {Array<{label: string, icon?: React.ReactNode, onClick: () => void, color?: string}>} actions
 * @param {string} [ariaLabel]
 * @param {number} [iconFontSize]
 */
export default function RowActionsMenu({actions, ariaLabel = 'Row actions', iconFontSize = 16}) {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    if (!actions || actions.length === 0) return null;

    const handleOpen = (e) => {
        e.stopPropagation();
        setAnchorEl(e.currentTarget);
    };
    const handleClose = () => setAnchorEl(null);

    return (
        <>
            <Tooltip title={ariaLabel}>
                <IconButton
                    size="small"
                    sx={{p: 0.25}}
                    onClick={handleOpen}
                    aria-label={ariaLabel}
                >
                    <MoreVertRounded sx={{fontSize: iconFontSize}}/>
                </IconButton>
            </Tooltip>
            <Menu
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                onClick={(e) => e.stopPropagation()}
                anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
                transformOrigin={{vertical: 'top', horizontal: 'right'}}
                // Actions like "Rename" move focus into a field elsewhere in the row (e.g. an
                // autoFocus TextField). Two MUI Modal behaviors fight that during the exit
                // transition: (1) enforceFocus keeps yanking focus back into the (still-mounted,
                // closing) menu, and (2) restoreFocus returns it to this menu's trigger button
                // once fully closed. Either one blurs the field and silently discards the
                // in-progress edit, so disable both — this menu never needs focus back.
                disableEnforceFocus
                disableRestoreFocus
            >
                {actions.map((action) => (
                    <MenuItem
                        key={action.label}
                        onClick={() => {
                            handleClose();
                            action.onClick();
                        }}
                        sx={{py: 0.75, color: action.color}}
                    >
                        {action.icon && (
                            <ListItemIcon sx={{minWidth: 28, color: action.color}}>
                                {action.icon}
                            </ListItemIcon>
                        )}
                        <ListItemText
                            primary={action.label}
                            slotProps={{primary: {sx: {fontSize: 13}}}}
                        />
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
}
